import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import { getHomeRouteForRole } from "../../utils/accessControl";
import "./RealgemsLogin.css";

const RealgemsLogin = () => {
  const navigate = useNavigate();
  const { login, loading, user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [transitionDurationMs, setTransitionDurationMs] = useState(5000);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleBannerError = (bannerId) => {
    setBanners((current) => current.filter((banner) => banner.id !== bannerId));
  };

  useEffect(() => {
    if (loading || !user) return;
    navigate(getHomeRouteForRole(user.access_type), { replace: true });
  }, [loading, navigate, user]);

  useEffect(() => {
    let mounted = true;

    const loadBanners = async () => {
      try {
        const response = await Api.get("/public/login-banners", {
          params: { portal: "REALGEMS" },
        });
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        const nextTransitionDuration = Number(response?.data?.transitionDurationMs);

        if (!mounted) return;

        setTransitionDurationMs(
          Number.isFinite(nextTransitionDuration) ? nextTransitionDuration : 5000
        );
        setBanners(items);
      } catch (err) {
        console.error("Erro ao carregar banners Realgems:", err);
      }
    };

    loadBanners();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setActiveBanner(0);
  }, [banners]);

  useEffect(() => {
    if (banners.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveBanner((current) => (current + 1) % banners.length);
    }, transitionDurationMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [banners, transitionDurationMs]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") || "").trim();
    const password = String(formData.get("password") || "");

    if (!email || !password) {
      setError("Preencha e-mail e senha para continuar.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await Api.post("/auth/login", {
        email,
        password,
        portal: "REALGEMS",
      });

      if (response.data.ok) {
        login(response.data.user);
        navigate(response.data.redirect, { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Nao foi possivel fazer login.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="realgems-login-page">
      <header className="realgems-login-topbar">
        <div className="realgems-login-brand">
          <img
            className="realgems-login-brand-smart"
            src="/logo-horizontal.png"
            alt="Smart Olfativos"
          />
          <img
            className="realgems-login-brand-partner"
            src="/logo-realgems.svg"
            alt="Realgems"
          />
        </div>

        <form className="realgems-topbar-form" onSubmit={handleSubmit}>
          <label className="sr-only" htmlFor="realgems-email">
            E-mail
          </label>
          <input
            id="realgems-email"
            name="email"
            type="email"
            placeholder="E-mail"
            autoComplete="username"
            disabled={submitting}
            required
          />

          <label className="sr-only" htmlFor="realgems-password">
            Senha
          </label>
          <input
            id="realgems-password"
            name="password"
            type="password"
            placeholder="Senha"
            autoComplete="current-password"
            disabled={submitting}
            required
          />

          <button type="submit" disabled={submitting}>
            {submitting ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </header>

      {error ? <div className="realgems-login-error-banner">{error}</div> : null}

      <main className="realgems-login-banner">
        <div className="realgems-login-carousel" role="region" aria-label="Carrossel Realgems">
          {banners.length ? (
            banners.map((banner, index) => (
              <picture
                key={banner.id || `realgems-banner-${index}`}
                className={`realgems-banner-slide ${index === activeBanner ? "is-active" : ""}`}
              >
                {banner.mobileUrl ? (
                  <source srcSet={banner.mobileUrl} media="(max-width: 768px)" />
                ) : null}
                <img
                  src={banner.url}
                  alt={banner.originalName || `Banner Realgems ${index + 1}`}
                  onError={() => handleBannerError(banner.id)}
                />
              </picture>
            ))
          ) : (
            <div className="realgems-banner-empty" aria-hidden="true" />
          )}
        </div>
      </main>
    </div>
  );
};

export default RealgemsLogin;
